<template>
    <transition
        name="fade"
    >
        <widget-position-layout v-if="showFloatingBeam || (isMobile && minimized === false)">
            <div class="floating-beam">
                <floating-beam-minimized v-if="minimized" @click="onOpenedBeamClicked()" />
                <floating-beam-opened v-else @close="onOpenedBeamClosed()" />
            </div>
        </widget-position-layout>
    </transition>
</template>

<script setup lang="ts">
    import useConfiguration from '@/composables/useConfiguration';
    import { onMounted, ref, watch } from 'vue';
    import useWindowSize from '@/composables/useWindowSize';
    import FloatingBeamMinimized from '@/components/FloatingBeamMinimized.vue';
    import WidgetPositionLayout from '@/components/layout/WidgetPositionLayout.vue';
    import FloatingBeamOpened from '@/components/FloatingBeamOpened.vue';
    import useWindowScroll from '@/composables/useWindowScroll';
    import useWidgetState, { State } from '@/composables/useWidgetState';
    import { Position, VisitorInteractionType } from '@keyvue/one-api-client';
    import Chat from '@/chat/Chat';
    import useVisitorData from '@/composables/useVisitorData';

    const chat = Chat.getInstance();
    const { assistent, isPreview } = useConfiguration();
    const { isMobile } = useWindowSize();
    const { state } = useWidgetState();
    const { scrollHeightPercentage, scrollHeightPossible } = useWindowScroll();
    const { registerInteraction } = useVisitorData();

    const showFloatingBeam = ref(false);
    const minimized = ref(!isPreview.value);

    function handleScroll() {
        // if scroll is not possible, show the beam
        if (!scrollHeightPossible.value) {
            showFloatingBeam.value = true;
            return;
        }

        if (chat.hasUserMessage.value) {
            showFloatingBeam.value = true;
            return;
        }

        // if we have an assistent
        if (assistent.value) {
            if (Position.BOTTOM.equals(assistent.value.widget.desktopPosition)
                || Position.BOTTOM_LEFT.equals(assistent.value.widget.desktopPosition)
                || Position.BOTTOM_RIGHT.equals(assistent.value.widget.desktopPosition)) {
                showFloatingBeam.value = true;
                return;
            }
        }

        if (!assistent.value?.widget.beamDisplayAfterPercentage) {
            return;
        }

        showFloatingBeam.value = scrollHeightPercentage.value >= assistent.value.widget.beamDisplayAfterPercentage;
    }

    watch(minimized, () => {
        // when opening the beam
        if (minimized.value === false) {
            // if there are user messages, we open the entire chat
            if (chat.hasUserMessage.value) {
                state.value = State.Opened;
            }
        }
    });

    watch(chat.hasUserMessage, () => {
        // if we have userMessages and are on desktop and state is minimized, unminimize it
        if (!isMobile.value && chat.hasUserMessage.value && state.value === State.Minimized) {
            minimized.value = true;
        }
    });

    onMounted(() => {
        // open big bar on desktop
        if (!isMobile.value && !chat.hasUserMessage.value) {
            minimized.value = false;
        }

        handleScroll();
    });

    watch(scrollHeightPercentage, handleScroll);

    async function onOpenedBeamClicked() {
        minimized.value = false;
        await registerInteraction(VisitorInteractionType.WIDGET_CLICKED);
    }

    function onOpenedBeamClosed() {
        minimized.value = true;
    }
</script>

<style scoped lang="scss">
    .floating-beam {
        width: 100%;
        filter: drop-shadow(4px 4px 6px rgba(0, 0, 0, 0.3));
        pointer-events: none;
        transition: transform 200ms ease;
        transform-origin: center center;

        &__container {
            position: fixed;
            user-select: none;
            display: flex;
            flex-direction: column;
            gap: 1rem;
            inset: 0;
        }
    }

    .fade-enter-active, .fade-leave-active {
        transition: opacity 200ms ease-in;
    }

    .fade-enter-from, .fade-leave-to {
        opacity: 0;
    }
</style>
